import { useEffect, useState } from 'react';
import { HandHelping, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Textarea } from '@/components/ui/Textarea';
import { CategoryGrid } from '@/components/tickets/NewTicketDialog';
import { WorkPhotoCapture } from './WorkPhotoCapture';
import { uploadCapture } from '@/features/workMode/hooks';
import { compressImage } from '@/features/workMode/images';
import { describeWorkError } from '@/features/workMode/api';
import type { WorkSession } from '@/features/workMode/types';
import { useSubmitWorkTicket, useWorkTicketCategories } from '@/features/tickets/hooks';
import { ticketRef } from '@/features/tickets/model';

interface WorkTicketDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  session: WorkSession;
  /** Fired after the request is filed so the bench can mark the list as seen. */
  onSubmitted?: (ticketId: string) => void;
}

const TITLE_PLACEHOLDER: Record<string, string> = {
  tool: 'e.g. New 10 mm drill bit for the CNC station',
  fixture: 'e.g. The bracket jig has play in it — needs a new one',
  purchase: 'e.g. Two boxes of M4 × 12 screws',
  maintenance: 'e.g. The press is leaking oil again',
  access: 'e.g. Access to the BOM pages in Atlas',
  it: 'e.g. The barcode scanner on Station 3 stopped pairing',
  prototype: 'e.g. A test piece of the new lid in grey',
  idea: 'e.g. Pre-sorted screw trays would save a minute a unit',
  other: 'e.g. Anything else you need from the office',
};

/**
 * "Request" on the bench. Different from a note: a note is an
 * observation that a manager reads; a request is a ticket with a status
 * the worker can follow in "My requests" until it is done.
 */
export function WorkTicketDialog({ open, onOpenChange, session, onSubmitted }: WorkTicketDialogProps) {
  const categoriesQ = useWorkTicketCategories(open);
  const submitTicket = useSubmitWorkTicket();
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [details, setDetails] = useState('');
  const [blocking, setBlocking] = useState(false);
  const [photos, setPhotos] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCategoryId(null);
    setTitle('');
    setDetails('');
    setBlocking(false);
    setPhotos([]);
  }, [open]);

  const categories = categoriesQ.data ?? [];
  const selected = categories.find((c) => c.id === categoryId) ?? null;

  const submit = async () => {
    if (!title.trim()) {
      toast.error('Say what you need — a few words is enough.');
      return;
    }
    setBusy(true);
    try {
      const paths: string[] = [];
      for (const f of photos) {
        const blob = await compressImage(f);
        paths.push(
          await uploadCapture(
            session.organization_id,
            session.id,
            blob,
            f.name.replace(/\.[^.]+$/, '') + '.jpg',
          ),
        );
      }
      const ticket = await submitTicket.mutateAsync({
        sessionId: session.id,
        title: title.trim(),
        categoryId,
        description: details,
        priority: blocking ? 'high' : 'medium',
        photoPaths: paths,
      });
      toast.success(`${ticketRef(ticket.number)} sent. Watch "Requests" for updates.`, {
        duration: 4000,
      });
      onSubmitted?.(ticket.id);
      onOpenChange(false);
    } catch (e) {
      toast.error(describeWorkError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto rounded-2xl p-0">
        <DialogHeader className="border-b border-border px-6 py-4">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <HandHelping className="h-5 w-5 text-info" />
            Ask for something
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5 px-6 py-5">
          <div className="space-y-2">
            <Label className="text-base">What kind of thing?</Label>
            <CategoryGrid
              categories={categories}
              value={categoryId}
              onChange={setCategoryId}
              loading={categoriesQ.isLoading}
              compact
            />
            {selected?.description && (
              <p className="text-sm text-muted-foreground">{selected.description}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="work-ticket-title" className="text-base">
              What do you need?
            </Label>
            <Input
              id="work-ticket-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={TITLE_PLACEHOLDER[selected?.slug ?? 'other'] ?? TITLE_PLACEHOLDER.other}
              maxLength={160}
              className="h-12 text-base"
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="work-ticket-details" className="text-base">
              Anything else? (optional)
            </Label>
            <Textarea
              id="work-ticket-details"
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="Which one, how many, where it goes, what it is for…"
              className="min-h-[96px] text-base"
            />
          </div>

          <button
            type="button"
            onClick={() => setBlocking((b) => !b)}
            aria-pressed={blocking}
            className={cn(
              'flex min-h-[56px] w-full items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors',
              blocking
                ? 'border-fresh-salmon-dark bg-fresh-salmon/25 text-fresh-salmon-dark'
                : 'border-border bg-card text-foreground hover:bg-secondary',
            )}
          >
            <Zap className="h-5 w-5 shrink-0" />
            <span className="min-w-0">
              <span className="block text-sm font-medium">This is holding me up</span>
              <span className="block text-xs text-muted-foreground">
                {blocking
                  ? 'Marked high priority — it will sit at the top of the inbox.'
                  : 'Tap if you cannot carry on without it.'}
              </span>
            </span>
          </button>

          <div className="space-y-2">
            <Label className="text-base">Photos (optional)</Label>
            <WorkPhotoCapture files={photos} onChange={setPhotos} max={4} disabled={busy} />
          </div>
        </div>
        <div className="flex items-center justify-between gap-3 border-t border-border px-6 py-4">
          <p className="hidden text-xs text-muted-foreground sm:block">
            Whoever handles it will route it and you will see every status change here.
          </p>
          <div className="ml-auto flex items-center gap-3">
            <Button variant="ghost" size="lg" onClick={() => onOpenChange(false)} disabled={busy}>
              Cancel
            </Button>
            <Button size="lg" onClick={submit} disabled={busy}>
              {busy ? 'Sending…' : 'Send request'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
